import { Pastille, type TonPastille } from '../ui/Primitives';
import {
  useFriseApprenant,
  type EvenementFrise,
  type NiveauFrise,
} from '../../lib/useEspaceApprenant';
import { ErreurApi } from '../../lib/api';

const PUCES: Record<NiveauFrise, string> = {
  danger: 'bg-red-500',
  warning: 'bg-amber-500',
  mint: 'bg-codialis-mint',
  info: 'bg-codialis-sky',
};

/** `JJ/MM/AAAA` à partir d'une date civile `AAAA-MM-JJ`. */
function dateCourte(iso: string): string {
  const [annee, mois, jour] = iso.split('-');
  return `${jour}/${mois}/${annee}`;
}

function cle(evenement: EvenementFrise, index: number): string {
  return `${evenement.date}-${evenement.categorie}-${index}`;
}

/**
 * Frise d'un dossier (CDC §8.3), du plus récent au plus ancien. Le même rendu
 * sert l'espace apprenant et le dossier lu par l'équipe : seule la source
 * change, et c'est l'API qui décide de ce que chacun voit.
 */
export function FriseEvenements({
  evenements,
  filtre = 'Toutes',
  vide,
}: {
  evenements: EvenementFrise[];
  filtre?: string;
  vide: string;
}): JSX.Element {
  const visibles =
    filtre === 'Toutes' ? evenements : evenements.filter((e) => e.categorie === filtre);

  return (
    <div className="relative pl-8">
      <div className="absolute bottom-2 left-2.5 top-2 w-px bg-gray-200" />
      <div className="space-y-6">
        {visibles.map((evenement, index) => (
          <div key={cle(evenement, index)} className="relative min-w-0">
            <div
              className={`absolute -left-[26px] top-1 h-4 w-4 rounded-full border-2 border-white ${
                PUCES[evenement.niveau]
              }`}
            />
            <div className="mb-1 flex flex-wrap items-center gap-2">
              <span className="font-mono text-xs text-codialis-gray">
                {dateCourte(evenement.date)}
              </span>
              <Pastille texte={evenement.categorie} ton={evenement.niveau as TonPastille} />
            </div>
            <div className="font-display font-bold text-codialis-navy">{evenement.titre}</div>
            <p className="break-words text-sm text-codialis-gray">{evenement.detail}</p>
          </div>
        ))}
        {visibles.length === 0 && (
          <p className="text-sm text-codialis-gray">
            {evenements.length === 0 ? vide : 'Aucun événement dans cette catégorie.'}
          </p>
        )}
      </div>
    </div>
  );
}

/**
 * Frise du dossier de l'apprenant connecté, servie par `/espace-apprenant/frise`.
 * L'API ne renvoie que le dossier rattaché au compte : il n'y a pas
 * d'identifiant à passer, donc pas de dossier d'un tiers à viser par erreur.
 */
export function ApprenantTimeline({ filtre = 'Toutes' }: { filtre?: string }): JSX.Element {
  const { data, isLoading, error } = useFriseApprenant();

  if (isLoading) {
    return <div className="text-sm text-codialis-gray">Chargement de votre frise…</div>;
  }
  if (error) {
    return (
      <div className="text-sm text-red-500">
        {error instanceof ErreurApi ? error.message : 'Votre frise n’a pas pu être chargée.'}
      </div>
    );
  }

  return (
    <FriseEvenements
      evenements={data ?? []}
      filtre={filtre}
      vide="Aucun événement n’est encore enregistré sur votre dossier."
    />
  );
}
